import { FC, useMemo, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { FiltersState, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  Download,
  Files,
  People,
  Settings,
  Upload,
} from '@factorialco/f0-react/icons/app'
import {
  CreateProjectDocument,
  CreateProjectMutation,
  CreateProjectMutationVariables,
  ProjectClientsDocument,
  ProjectLegalEntitiesDocument,
  ProjectManagementProjectEmployeesAssignmentEnum,
  ProjectManagementProjectFilter,
  ProjectsDataCollectionDocument,
  ProjectsDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { getBillableLabel } from 'modules/project_management/components/labelTags/BillableTag'
import {
  getStatusColor,
  getStatusLabel,
} from 'modules/project_management/components/labelTags/StatusTag'
import { ProjectIdWithPermissions } from 'modules/project_management/components/modals/BulkAssignProjectRolesModal'
import AddProjectWizard from 'modules/project_management/components/wizards/AddProjectWizard'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { AssignClientModal } from './modals/AssignClientsModal'
import { AssignEmployeesModal } from './modals/AssignEmployeesModal'
import { AssignRolesModal } from './modals/AssignRolesModal'

const MAX_AVATARS = 4

const MODAL: {
  [key in BulkActionType]: React.ComponentType<{
    ids: number[]
    onCloseModal?: () => void
    initialEmployeesAssignment?: ProjectManagementProjectEmployeesAssignmentEnum | undefined
    projectIdWithPermissions?: ProjectIdWithPermissions[]
  }>
} = {
  ['AssignEmployees']: AssignEmployeesModal,
  ['AssignClient']: AssignClientModal,
  ['AssignRoles']: AssignRolesModal,
}

type BulkActionType = 'AssignEmployees' | 'AssignClient' | 'AssignRoles'

interface ProjectsDataCollectionProps {
  canSeeCosts: boolean
  canCreateProjects: boolean
  canDeleteProjects?: boolean
  canManageProjects: boolean

  hasBulkActionsFeature?: boolean
  hasClientsFeature: boolean
  hasCostsFeature: boolean
  hasExpensesFeature: boolean
  hasFlexibleTrackingFeature?: boolean
  hasImportFeature?: boolean
  hasSubprojectsFeature?: boolean
  hasTimeTrackingSection?: boolean
  clientId?: number | null

  onClickCreateClientProject?: () => void
  onClickLinkClientProject?: () => void
  onClickUnlinkProject?: (projectIds: number[]) => void
}

type GenericInFilter = {
  label: string
  type: 'in'
  options: {
    cache: boolean
    options: () => Promise<Array<{ label: string; value: boolean | number | string }>>
  }
}

const resolveFilters = (
  filters: FiltersState<{
    isBillable: GenericInFilter
    clientIds: GenericInFilter
    legalEntityIds: GenericInFilter
    status: GenericInFilter
  }>
): ProjectManagementProjectFilter | undefined => {
  const filter: ProjectManagementProjectFilter = {}

  const isBillable = filters?.isBillable?.map(Boolean) || []
  if (isBillable.length === 1) {
    filter.isBillable = { eq: isBillable[0] }
  }

  const clientIds = filters?.clientIds?.map(Number).filter(Boolean) || []
  if (clientIds.length) {
    filter.clientId = { in: clientIds }
  }

  const legalEntityIds = filters?.legalEntityIds?.map(Number).filter(Boolean) || []
  if (legalEntityIds.length) {
    filter.legalEntityId = { in: legalEntityIds }
  }

  const statuses = filters?.status?.map(String).filter(Boolean) || []
  if (statuses.length) {
    filter.status = { in: statuses }
  }

  return filter
}

const ProjectsDataCollection: FC<ProjectsDataCollectionProps> = ({
  canSeeCosts,
  canCreateProjects,
  canDeleteProjects = false,
  canManageProjects,
  clientId = null,
  hasBulkActionsFeature = false,
  hasClientsFeature,
  hasCostsFeature = false,
  hasExpensesFeature = false,
  hasFlexibleTrackingFeature = false,
  hasImportFeature = false,
  hasSubprojectsFeature = false,
  hasTimeTrackingSection = false,
  onClickCreateClientProject,
  onClickLinkClientProject,
  onClickUnlinkProject,
}) => {
  const {
    access: { locale },
    employee,
    company: { id: companyId },
  } = useCurrentSession()

  const originClientSection = clientId ? true : false

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const apolloClient = useApolloClient()

  const roleIdsFilter = useEmployeesFilter({
    label: i18n.t('project_management.table.projects_list.filters.roled_by'),
  })

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasExpensesFeature || hasFlexibleTrackingFeature || hasTimeTrackingSection)

  const showDuplicateProject = canCreateProjects && hasSubprojectsFeature

  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([])
  const [initialEmployeesAssignment, setInitialEmployeesAssignment] = useState<
    ProjectManagementProjectEmployeesAssignmentEnum | undefined
  >(undefined)

  const [projectIdWithPermissions, setProjectIdWithPermissions] = useState<
    ProjectIdWithPermissions[]
  >([])

  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false)

  const [actionType, setActionType] = useState<BulkActionType | null>(null)

  const ActionModal = useMemo(() => (actionType ? MODAL[actionType] : null), [actionType])
  const onCloseModal = () => setActionType(null)

  const taskProgressFormatting = (
    completedTasks: number | null | undefined,
    allTasks: number | null | undefined
  ) => {
    const completed = completedTasks || 0
    if (allTasks && allTasks > 0) {
      return completed + '/' + allTasks
    }
    return undefined
  }

  const source = useGraphqlDataCollectionSource({
    document: ProjectsDataCollectionDocument,
    mapVariables: ({ filters, search, sortings }) => ({
      canSeeCosts,
      clientIds: clientId ? [clientId] : undefined,
      hasClients: hasClientsFeature,
      hasCosts: hasCostsFeature,
      hasSpendings: hasExpensesFeature,
      nameOrCode: search,
      sortOrder: sortings?.[0],
      maxProjectWorkers: MAX_AVATARS,
      filter: resolveFilters(filters),
      roledByEmployeeIds: filters?.roleIds?.map(Number).filter(Boolean) || [],
      roleTypes: filters?.roleTypes?.map(String).filter(Boolean) || [],
    }),
    mapData: (data) => data.projectManagement.projectsConnection.nodes,
    mapPagination: (data) => ({
      total: data.projectManagement.projectsConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.table.projects_list.total_items', { count: totals })
    },
    itemUrl: ({ id }) => navigation.projects.id.fullPath({ id }),
    itemActions: ({ id, name, _policies }) => {
      const canAssignEmployees = !!_policies?.assignUnassignProjectWorkers.can
      if (!(canAssignEmployees || canDeleteProjects || canManageProjects || showDuplicateProject)) {
        return []
      }

      return !originClientSection
        ? compact([
            canAssignEmployees && {
              label: i18n.t('project_management.table.projects_list.menu_links.assign_employees'),
              icon: People,
              onClick: () => {
                track(trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectStart)
                navigate(navigation.projects.id.details.employees.workers.fullPath({ id }))
              },
            },
            showDuplicateProject && {
              label: i18n.t('project_management.table.projects_list.menu_links.duplicate_project'),
              icon: Files,
              onClick: () => {
                navigate(navigation.projects.list.projectId.duplicate.fullPath({ projectId: id }), {
                  state: { projectName: name, hasSubprojectsFeature },
                })
              },
            },
            canManageProjects && {
              label: i18n.t('project_management.table.projects_list.menu_links.settings'),
              icon: Settings,
              onClick: () => {
                navigate(navigation.projects.id.settings.fullPath({ id }))
              },
            },
            canDeleteProjects && {
              label: i18n.t('project_management.table.projects_list.menu_links.delete_project'),
              icon: Delete,
              onClick: () => {
                navigate(navigation.projects.list.projectId.delete.fullPath({ projectId: id }), {
                  state: { projectName: name },
                })
              },
              critical: true,
            },
          ])
        : compact([
            {
              label: i18n.t('finance.contacts.contact_details.projects.unlink_project'),
              critical: true,
              onClick: () => {
                track(trackingEvents.projectManagement.clients.list.clientListProjectUnlink)
                onClickUnlinkProject?.([id])
              },
            },
          ])
    },
    primaryActions: () =>
      canCreateProjects && !originClientSection
        ? {
            label: i18n.t('project_management.add_button'),
            icon: Add,
            onClick: () => {
              setShowCreateProjectModal(true)
            },
          }
        : undefined,
    secondaryActions: () =>
      canManageProjects && originClientSection
        ? compact([
            canManageProjects
              ? {
                  label: i18n.t(
                    'finance.contacts.contact_details.projects.no_projects_message.options.new'
                  ),
                  onClick: () => {
                    onClickCreateClientProject?.()
                  },
                }
              : null,
            canManageProjects
              ? {
                  label: i18n.t(
                    'finance.contacts.contact_details.projects.no_projects_message.options.link'
                  ),
                  onClick: () => {
                    onClickLinkClientProject?.()
                  },
                }
              : null,
          ])
        : canExportData || canCreateProjects
        ? compact([
            canCreateProjects && hasImportFeature
              ? {
                  label: i18n.t(
                    'project_management.table.projects_list.table_options.import.title'
                  ),
                  description: i18n.t(
                    'project_management.table.projects_list.table_options.import.description'
                  ),
                  icon: Upload,
                  onClick: () => {
                    track(trackingEvents.projectManagement.importProjects.importProjectsStart)
                    navigate(navigation.projects.list.import.fullPath())
                  },
                }
              : null,
            canExportData
              ? {
                  label: i18n.t(
                    'project_management.table.projects_list.table_options.export.title'
                  ),
                  description: i18n.t(
                    'project_management.table.projects_list.table_options.export.description'
                  ),
                  icon: Download,
                  onClick: () => {
                    track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                    navigate(navigation.projects.list.export.fullPath())
                  },
                }
              : null,
          ])
        : undefined,
    selectable: hasBulkActionsFeature ? (item) => item.id : undefined,
    bulkActions: hasBulkActionsFeature
      ? (selectedItems) => {
          const canAssignEmployees = selectedItems.itemsStatus
            .map((itemStatus) => !!itemStatus.item._policies?.assignUnassignProjectWorkers.can)
            .every((can) => can)

          return !originClientSection
            ? {
                primary: compact([
                  canAssignEmployees
                    ? {
                        id: 'assign-people',
                        label: i18n.t(
                          'project_management.table.projects_list.menu_links.assign_employees'
                        ),
                      }
                    : null,
                ]),
                secondary: compact([
                  hasClientsFeature
                    ? {
                        id: 'assign-client',
                        label: i18n.t(
                          'project_management.table.projects_list.menu_links.assign_client'
                        ),
                      }
                    : null,
                  {
                    id: 'assign-roles',
                    label: i18n.t('project_management.table.projects_list.menu_links.assign_roles'),
                  },
                ]),
              }
            : {
                primary: compact([
                  {
                    id: 'unlink-project',
                    label: i18n.t('finance.contacts.contact_details.projects.unlink_project'),
                    critical: true,
                  },
                ]),
              }
        }
      : undefined,
    filters: {
      isBillable: {
        label: i18n.t('project_management.table.projects_list.headers.billing'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('project_management.projects.is_billable.billable'), value: true },
            {
              label: i18n.t('project_management.projects.is_billable.non_billable'),
              value: false,
            },
          ],
        },
      },
      clientIds: {
        label: i18n.t('project_management.table.projects_list.headers.client'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await apolloClient.query({
              query: ProjectClientsDocument,
              fetchPolicy: 'cache-first',
              variables: {
                companyId,
              },
            })

            return (
              result.data?.finance.contactsConnection.edges.map((contactNode) => ({
                label: contactNode.node.name,
                value: contactNode.node.id,
              })) || []
            )
          },
        },
      },
      legalEntityIds: {
        label: i18n.t('project_management.table.projects_list.headers.legal_entity'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await apolloClient.query({
              query: ProjectLegalEntitiesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.companies.legalEntitiesConnection.edges.map((legalEntityNode) => ({
                label: legalEntityNode.node.legalName,
                value: legalEntityNode.node.id,
              })) || []
            )
          },
        },
      },
      status: {
        label: i18n.t('project_management.table.projects_list.headers.status'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('project_management.projects.status.active'),
              value: 'active',
            },
            {
              label: i18n.t('project_management.projects.status.draft'),
              value: 'draft',
            },
            {
              label: i18n.t('project_management.projects.status.processing'),
              value: 'processing',
            },
            {
              label: i18n.t('project_management.projects.status.closed'),
              value: 'closed',
            },
          ],
        },
      },
      roleTypes: {
        label: i18n.t('project_management.table.projects_list.filters.role_type'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('project_management.roles.owner.title'),
              value: 'owner',
            },
            {
              label: i18n.t('project_management.roles.editor.title'),
              value: 'editor',
            },
            {
              label: i18n.t('project_management.roles.viewer.title'),
              value: 'viewer',
            },
          ],
        },
      },
      roleIds: roleIdsFilter,
    },
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.projects_list.headers.name'),
      },
      code: {
        label: i18n.t('project_management.table.projects_list.headers.code'),
      },
      startDate: {
        label: i18n.t('project_management.table.projects_list.headers.startDate'),
      },
      dueDate: {
        label: i18n.t('project_management.table.projects_list.headers.dueDate'),
      },
      status: {
        label: i18n.t('project_management.table.projects_list.headers.status'),
      },
    },
    defaultSortings: {
      field: 'name',
      order: 'desc',
    },
  })

  const onCreateProjectSuccess = (projectId: number) => {
    setShowCreateProjectModal(false)
    navigate(navigation.projects.id.fullPath({ id: projectId }), { state: { showCrmUpsell: true } })
  }

  const [createProject] = useMutation(CreateProjectDocument, {
    extractorFn: (data: CreateProjectMutation) =>
      data.projectManagementMutations.createProjectmanagementProject,
  })

  const wrappedCreateProject = (dto: CreateProjectMutationVariables) =>
    createProject({ variables: dto })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems) => {
          const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
          setSelectedProjectIds(selectedCheckedItems.map((itemStatus) => itemStatus.item.id))

          if (!selectedProjectIds.length) {
            return
          }

          switch (action) {
            case 'assign-people':
              track(
                trackingEvents.projectManagement.bulkActions.employeeProjectsAssignment.eventStart
              )
              setActionType('AssignEmployees')
              if (selectedItems.itemsStatus.length === 1) {
                setInitialEmployeesAssignment(selectedCheckedItems[0]?.item.employeesAssignment)
              }
              break
            case 'assign-client':
              track(trackingEvents.projectManagement.bulkActions.clientAssignment.eventStart)
              setActionType('AssignClient')
              break
            case 'assign-roles':
              track(trackingEvents.projectManagement.bulkActions.roleProjectsAssignment.eventStart)
              setActionType('AssignRoles')
              setProjectIdWithPermissions(
                selectedCheckedItems.map((itemStatus) => ({
                  id: itemStatus.item.id,
                  canAssignOwners: !!itemStatus.item._policies?.assignOwner?.can,
                }))
              )
              break
            case 'unlink-project':
              onClickUnlinkProject?.(selectedCheckedItems.map((itemStatus) => itemStatus.item.id))
              break
          }
        }}
        emptyStates={{
          'no-data': {
            title: i18n.t('finance.contacts.contact_details.projects.no_projects_message.title'),
            description: i18n.t(
              'finance.contacts.contact_details.projects.no_projects_message.description'
            ),
            emoji: '📄',
            actions: [
              {
                label: i18n.t(
                  'finance.contacts.contact_details.projects.no_projects_message.button'
                ),
                variant: 'default',
                onClick: () => {
                  originClientSection
                    ? onClickCreateClientProject?.()
                    : setShowCreateProjectModal(true)
                },
              },
              ...(originClientSection
                ? compact([
                    {
                      label: i18n.t(
                        'finance.contacts.contact_details.projects.no_projects_message.options.link'
                      ),
                      variant: 'outline' as const,
                      onClick: () => {
                        onClickLinkClientProject?.()
                      },
                    },
                  ])
                : compact([])),
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.projects_list.headers.name'),
                  render: ({ name }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: name,
                  }),
                  sorting: 'name' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.code'),
                  render: ({ code }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: code ?? undefined,
                  }),
                  sorting: 'code' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.employees'),
                  render: ({ projectworkersConnection }: ProjectsDataCollectionItemFragment) => ({
                    type: 'avatarList',
                    value: {
                      avatarList: projectworkersConnection.nodes.map((node) => ({
                        type: 'person',
                        firstName: node.employee?.firstName ?? '',
                        lastName: node.employee?.lastName ?? '',
                        src: node.employee?.access.avatar
                          ? buildUrl(node.employee.access.avatar)
                          : undefined,
                      })),
                      max: MAX_AVATARS,
                    },
                  }),
                },
                hasClientsFeature && !originClientSection
                  ? {
                      label: i18n.t('project_management.table.projects_list.headers.client'),
                      render: ({ client }: ProjectsDataCollectionItemFragment) => ({
                        type: 'text',
                        value: contactFullName(client),
                      }),
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.projects_list.headers.hours'),
                  render: ({ inputedMinutes }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: formatProjectHours(inputedMinutes),
                  }),
                },
                {
                  label: i18n.t('project_management.tasks.analytics.task_completed'),
                  render: ({ projecttasksConnection }: ProjectsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: taskProgressFormatting(
                      projecttasksConnection.nodes.filter((node) => node?.task?.status === 'done')
                        .length,
                      projecttasksConnection.totalCount
                    ),
                  }),
                  align: 'right',
                },
                hasCostsFeature
                  ? {
                      label: i18n.t('project_management.table.projects_list.headers.billing'),
                      render: ({ isBillable }: ProjectsDataCollectionItemFragment) => ({
                        type: 'tag',
                        value: {
                          label: getBillableLabel(!!isBillable),
                        },
                      }),
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.projects_list.headers.status'),
                  render: ({ status }: ProjectsDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      label: getStatusLabel(status),
                      status: getStatusColor(status),
                    },
                  }),
                  sorting: 'status' as const,
                },
                hasCostsFeature
                  ? {
                      label: i18n.t(
                        'project_management.overview.sections.financial_details.insights.cost_to_date.title'
                      ),
                      render: ({
                        fixedCostCents,
                        laborCostCents,
                        spendingCostCents,
                        legalEntity,
                      }: ProjectsDataCollectionItemFragment) => {
                        const projectCost =
                          Number(fixedCostCents || 0) +
                          Number(laborCostCents || 0) +
                          Number(spendingCostCents || 0)

                        return {
                          type: 'text',
                          value:
                            !!projectCost && isCurrency(legalEntity?.currency)
                              ? MoneyWithCurrency({
                                  amount: projectCost,
                                  currency: legalEntity?.currency,
                                  locale,
                                })
                              : '-',
                        }
                      },
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.projects_list.headers.startDate'),
                  render: ({ startDate }: ProjectsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: startDate ? new Date(startDate) : undefined,
                  }),
                  sorting: 'startDate' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.dueDate'),
                  render: ({ dueDate }: ProjectsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: dueDate ? new Date(dueDate) : undefined,
                  }),
                  sorting: 'dueDate' as const,
                },
              ]),
            },
          },
        ]}
      />
      <Outlet
        context={{
          hasExpensesModule: hasExpensesFeature,
          hasFlexibleTracking: hasFlexibleTrackingFeature,
          hasImportProjectsEnabled: hasImportFeature,
        }}
      />
      {ActionModal && (
        <ActionModal
          onCloseModal={onCloseModal}
          ids={selectedProjectIds}
          initialEmployeesAssignment={initialEmployeesAssignment}
          projectIdWithPermissions={projectIdWithPermissions}
        />
      )}
      {showCreateProjectModal && (
        <AddProjectWizard
          onClose={() => setShowCreateProjectModal(false)}
          onSuccess={onCreateProjectSuccess}
          create={wrappedCreateProject}
        />
      )}
    </>
  )
}

export default ProjectsDataCollection
